import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, MailIcon, InstagramIcon, ArrowLeftIcon } from 'lucide-react';
import { PLACES, KINDS, cateringFor } from '../data/smaken';
import { verdictFor } from '../data/guide';
import { shopByPlace } from '../data/shop';
import { CateringCard } from '../components/CateringCard';
import { ShopCard } from '../components/ShopCard';
import { Rating } from '../components/Rating';
import { StampBadge } from '../components/StampBadge';
import { MiniMap } from '../components/SmalandMap';
import { PlaceCard } from '../components/PlaceCard';
import { Reveal } from '../components/Reveal';
import { ArrowMark, Note, Stamp } from '../components/Marks';
const ACTION_LABEL: Record<string, string> = {
  boka: 'Boka bord',
  bestall: 'Beställ',
  besok: 'Hitta hit'
};
export function PlacePage() {
  const {
    slug
  } = useParams();
  const place = PLACES.find(p => p.slug === slug);
  if (!place) {
    return <div className="mx-auto max-w-page px-5 py-32 text-center md:px-8" data-id="element-1014" data-mp="8scs2wse3m1r">
        <p className="display-xl text-4xl uppercase" data-id="element-1015" data-mp="8ufqo57lyni3">Hittade inte hit.</p>
        <Link to="/upptack" className="mt-8 inline-block bg-rod px-8 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1016" data-mp="cupvvfnkt9vw">
          Tillbaka till Upptäck
        </Link>
      </div>;
  }
  const accent = KINDS.find(k => k.id === place.kind)?.accent ?? '#E42616';
  const related = PLACES.filter(p => p.slug !== place.slug && p.kind === place.kind).slice(0, 3);
  const catering = cateringFor(place.slug);
  const verdict = verdictFor(place.slug);
  const shopItems = shopByPlace(place.slug);
  return <article data-id="element-1017" data-mp="15i3xhr27ytmc">
      {/* ------------------------------- Masthead ------------------------------- */}
      <div className="mx-auto max-w-page px-5 pb-8 pt-8 md:px-8 md:pt-10" data-id="element-1018" data-mp="1aj9nhh4hw2s6">
        <Link to="/upptack" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide2 text-black/50 transition-colors duration-150 ease-out hover:text-rod" data-id="element-1019" data-mp="1fhuq1n77yyjs">
          <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1020" data-mp="kv4cmwxaxakq" />
          Upptäck
        </Link>

        <div className="mt-6 grid items-end gap-6 lg:grid-cols-[minmax(0,1.6fr),minmax(0,1fr)]" data-id="element-1021" data-mp="zk0jiqnmghjr">
          <div data-id="element-1022" data-mp="uq62juzeigod">
            {place.stamp && <Stamp className="mb-4" data-id="element-1023" data-mp="18j5drks5hoq4">{place.stamp}</Stamp>}
            <h1 className="display-xl text-[3rem] uppercase leading-[0.9] sm:text-[4.5rem]" data-id="element-1024" data-mp="18hkj7v5u65s9">{place.name}</h1>
            <p className="mt-4 text-[12px] font-bold uppercase tracking-wide2" style={{
            color: accent
          }} data-id="element-1025" data-mp="13jssh4gxgppn">
              {place.city} · {place.tags.join(' · ')}
            </p>
            {verdict && <div className="mt-5" data-id="element-1026" data-mp="1dj033jwa2692">
                <Rating value={verdict.rating} data-id="element-1027" data-mp="3k8ep4oc423dy" />
                <p className="mt-2 font-hand text-2xl text-rod" data-id="element-1028" data-mp="3k97zs0e6xd1d">Beställ: {verdict.order}</p>
              </div>}
          </div>

          <div className="flex flex-wrap gap-3 lg:justify-end" data-id="element-1029" data-mp="1urkjmu840zjd">
            {catering.length > 0 ? <a href="#catering" className="bg-rod px-7 py-3.5 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1030" data-mp="1zqdzuues74n8">
                Beställ catering
              </a> : <a href={`mailto:${place.email ?? 'hej@smaken.se'}`} className="bg-black px-7 py-3.5 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1031" data-mp="1c12vz424wu4v">
                {ACTION_LABEL[place.action]}
              </a>}
            <a href="#hitta-hit" className="border border-black px-7 py-3.5 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1032" data-mp="1731aydl5ekqr">
              Visa på karta
            </a>
          </div>
        </div>
      </div>

      {/* -------------------------------- Gallery -------------------------------- */}
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-1033" data-mp="wfyi6c7j7mpd">
        <div className="relative grid gap-3 sm:grid-cols-[minmax(0,2fr),minmax(0,1fr)]" data-id="element-1034" data-mp="sctisiir014y">
          <StampBadge className="absolute -top-5 left-4 z-10 w-36 sm:-top-7 sm:w-44" data-id="element-1035" data-mp="ndyzlfgvcakb" />
          <img src={place.image} alt={place.name} className="aspect-[4/3] w-full object-cover sm:aspect-[3/2]" data-id="element-1036" data-mp="ngbetkv8s6to" />
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-1" data-id="element-1037" data-mp="dndt75uye1qu">
            {place.gallery.slice(0, 3).map(src => <img key={src} src={src} alt="" loading="lazy" className="aspect-square w-full object-cover sm:aspect-[3/2]" data-id="element-1038" data-mp="8pca4kxj22fd" />)}
          </div>
        </div>
      </div>

      {/* --------------------------------- Catering -------------------------------- */}
      {catering.length > 0 && <section id="catering" aria-labelledby="catering-rubrik" className="mt-16 border-y-2 border-black" data-id="element-1039" data-mp="26cetumtabzuc">
          <div className="mx-auto max-w-page px-5 py-14 md:px-8 md:py-16" data-id="element-1040" data-mp="2bduck87zpu9k">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-1041" data-mp="2gbcehenlb982">
              <div data-id="element-1042" data-mp="2kbegxq15g6oe">
                <h2 id="catering-rubrik" className="display-xl text-4xl uppercase sm:text-5xl" data-id="element-1043" data-mp="2kbwagewfg6sm">
                  Catering från {place.name}
                </h2>
                <Note className="mt-3 block" data-id="element-1044" data-mp="1skqiilqv1fef">Lägg till i din förfrågan — betala inget nu.</Note>
              </div>
              <Link to="/forfragan" className="group flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:text-rod" data-id="element-1045" data-mp="1wlsij4slpvhg">
                Till förfrågan
                <ArrowMark className="h-4 w-9 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1046" data-mp="18x9beslafoqy" />
              </Link>
            </div>

            <div className="mt-10 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1047" data-mp="2c6lsj38p89pd">
              {catering.map((offer, i) => <Reveal key={offer.id} delay={Math.min(i * 0.05, 0.15)} data-id="element-1048" data-mp="27794if63cwl7" data-mpi={i}>
                  <CateringCard offer={offer} showPlace={false} data-id="element-1049" data-mp="2lx6oeiwzn04l" />
                </Reveal>)}
            </div>
          </div>
        </section>}

      {/* ---------------------------------- Shop ---------------------------------- */}
      {shopItems.length > 0 && <section aria-labelledby="butik-rubrik" className="mx-auto max-w-page px-5 py-14 md:px-8 md:py-16" data-id="element-1050" data-mp="1obizsk6rxm3m">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-1051" data-mp="22bk09yj4mrik">
            <div data-id="element-1052" data-mp="1y7c9zwx0t3i8">
              <p className="text-[11px] font-bold uppercase tracking-wide3 text-blaa" data-id="element-1053" data-mp="1y6u9go3bs34i">Butiken</p>
              <h2 id="butik-rubrik" className="display-xl mt-2 text-4xl uppercase sm:text-5xl" data-id="element-1054" data-mp="3dnkxm1u23o9x">
                Ta hem något
              </h2>
            </div>
            <Link to="/butik" className="group flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:text-blaa" data-id="element-1055" data-mp="3h6k08g6y4sj2">
              Hela butiken
              <ArrowMark className="h-4 w-9 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1056" data-mp="37aqgabqwdwr9" />
            </Link>
          </div>

          <div className="mt-10 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1057" data-mp="3rneogwmxau1">
            {shopItems.map((product, i) => <Reveal key={product.id} delay={Math.min(i * 0.05, 0.15)} data-id="element-1058" data-mp="8pyqgx6hpvew" data-mpi={i}>
                <ShopCard product={product} data-id="element-1059" data-mp="3q8jjlf5fo8p6" />
              </Reveal>)}
          </div>
        </section>}

      {/* -------------------------------- Editorial ------------------------------- */}
      <div className="mx-auto grid max-w-page gap-12 px-5 py-16 md:px-8 md:py-20 lg:grid-cols-[minmax(0,1.5fr),minmax(0,1fr)] lg:gap-20" data-id="element-1060" data-mp="mpzz4yszy74x">
        <div data-id="element-1061" data-mp="rjui200s2i2y">
          <Reveal data-id="element-1062" data-mp="2nkw6lv1mzaqu">
            <section aria-labelledby="om" data-id="element-1063" data-mp="2nlfqviereb81">
              <h2 id="om" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1064" data-mp="2nmh0rjk1gro6">
                Om
              </h2>
              <p className="mt-4 font-display text-2xl leading-snug sm:text-3xl" data-id="element-1065" data-mp="2eslxyi6e3dbp">{place.blurb}</p>
              {place.description.map(paragraph => <p key={paragraph} className="mt-5 text-lg leading-relaxed text-black/70" data-id="element-1066" data-mp="29v2ixg1vawip">
                  {paragraph}
                </p>)}
            </section>
          </Reveal>

          <Reveal delay={0.06} data-id="element-1067" data-mp="32jhs3lybikm4">
            <section aria-labelledby="gillar" className="mt-14 border-t-2 border-black pt-8" data-id="element-1068" data-mp="32gc64fkzjyum">
              <div className="flex items-baseline gap-3" data-id="element-1069" data-mp="2xl6i9b80wa3w">
                <h2 id="gillar" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1070" data-mp="2sixssouczq6j">
                  Det här gillar vi
                </h2>
                <Note className="!text-lg" data-id="element-1071" data-mp="1h53ot68tg976">Vår åsikt, inte deras.</Note>
              </div>
              <ul className="mt-6 space-y-4" data-id="element-1072" data-mp="170x38cnezlev">
                {place.weLike.map(item => <li key={item} className="flex items-start gap-4" data-id="element-1073" data-mp="1bzzv0vedvv4w">
                    <ArrowMark className="mt-2 h-4 w-10 shrink-0" color={accent} data-id="element-1074" data-mp="1c09nqqqtcgea" />
                    <span className="text-lg leading-relaxed" data-id="element-1075" data-mp="1gyb7ldw2cjqt">{item}</span>
                  </li>)}
              </ul>
            </section>
          </Reveal>

          {place.menu && <Reveal delay={0.06} data-id="element-1076" data-mp="1pqljczmlwxa6">
              <section aria-labelledby="meny" className="mt-14 border-t-2 border-black pt-8" data-id="element-1077" data-mp="1ptr4k3s47f0m">
                <h2 id="meny" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1078" data-mp="1usauef9txay9">
                  Meny / Utbud
                </h2>
                <ul className="mt-6" data-id="element-1079" data-mp="24nm76s1og83d">
                  {place.menu.map(item => <li key={item.name} className="flex items-baseline justify-between gap-6 border-b border-black/15 py-4" data-id="element-1080" data-mp="5cbgylz6gzct">
                      <span data-id="element-1081" data-mp="2zfqvrel9mj2y">
                        <span className="font-display text-xl" data-id="element-1082" data-mp="2zgilw9w3folu">{item.name}</span>
                        <span className="mt-0.5 block text-sm text-black/55" data-id="element-1083" data-mp="34fbrlg552bbg">{item.note}</span>
                      </span>
                      <span className="shrink-0 font-display text-lg" data-id="element-1084" data-mp="38ec98vbvr7wv">{item.price}</span>
                    </li>)}
                </ul>
              </section>
            </Reveal>}
        </div>

        {/* --------------------------------- Facts --------------------------------- */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1085" data-mp="3nbbpt8opmb3h">
          <Reveal delay={0.06} data-id="element-1086" data-mp="3s4xqe2hlmoix">
            <section id="hitta-hit" aria-labelledby="hitta" className="border-2 border-black" data-id="element-1087" data-mp="3s4o8z3amdpou">
              <MiniMap city={place.city} accent={accent} className="w-full border-b-2 border-black" data-id="element-1088" data-mp="3w4onenxpy0vw" />
              <div className="p-6" data-id="element-1089" data-mp="50zyl4qjo3t4">
                <h2 id="hitta" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1090" data-mp="9y06ll6yfgmv">
                  Hitta hit
                </h2>
                <p className="mt-3 flex items-start gap-2.5 leading-relaxed" data-id="element-1091" data-mp="k3rly26ujjbz">
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-black/40" aria-hidden="true" data-id="element-1092" data-mp="f22esxmqvpt6" />
                  {place.address}
                </p>

                <h2 className="mt-8 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1093" data-mp="62f9i0qw9zuu">Öppet</h2>
                <ul className="mt-3 space-y-1.5" data-id="element-1094" data-mp="12sesyq8l2is7">
                  {place.hours.map(row => <li key={row.day} className="flex justify-between gap-4 text-sm" data-id="element-1095" data-mp="xtv4oj6iouwo">
                      <span className="text-black/60" data-id="element-1096" data-mp="xvxvh5fhus2g">{row.day}</span>
                      <span className="font-medium" data-id="element-1097" data-mp="t120bweynol4">{row.time}</span>
                    </li>)}
                </ul>

                {(place.email || place.instagram) && <>
                    <h2 className="mt-8 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1098" data-mp="1llacuskz66fu">
                      Kontakt
                    </h2>
                    <ul className="mt-3 space-y-2 text-sm" data-id="element-1099" data-mp="3b1cmdargvajm">
                      {place.email && <li className="flex items-center gap-2.5" data-id="element-1100" data-mp="3fycudr7vmndd">
                          <MailIcon className="h-4 w-4 shrink-0 text-black/40" aria-hidden="true" data-id="element-1101" data-mp="31armlk023zz4" />
                          <a href={`mailto:${place.email}`} className="transition-colors duration-150 ease-out hover:text-rod" data-id="element-1102" data-mp="369b9pnqdzht2">
                            {place.email}
                          </a>
                        </li>}
                      {place.instagram && <li className="flex items-center gap-2.5" data-id="element-1103" data-mp="2ie06fjfta0za">
                          <InstagramIcon className="h-4 w-4 shrink-0 text-black/40" aria-hidden="true" data-id="element-1104" data-mp="2nb8qxqofmh3p" />
                          {place.instagram}
                        </li>}
                    </ul>
                  </>}
              </div>
            </section>
          </Reveal>
        </aside>
      </div>

      {/* ------------------------------- Discover more ------------------------------ */}
      {related.length > 0 && <section aria-labelledby="mer" className="border-t border-black/12" data-id="element-1105" data-mp="2frhikt1vpq31">
          <div className="mx-auto max-w-page px-5 py-16 md:px-8 md:py-20" data-id="element-1106" data-mp="2b0r7cz6rut0f">
            <h2 id="mer" className="display-xl text-3xl uppercase sm:text-5xl" data-id="element-1107" data-mp="2ayo8dpnn5pjk">
              Upptäck mer
            </h2>
            <div className="mt-10 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1108" data-mp="2kqki5xz3b43m">
              {related.map((other, i) => <Reveal key={other.slug} delay={Math.min(i * 0.06, 0.18)} data-id="element-1109" data-mp="2yor187zdqzj8" data-mpi={i}>
                  <PlaceCard place={other} data-id="element-1110" data-mp="2tqpgzjq7wu66" />
                </Reveal>)}
            </div>
          </div>
        </section>}
    </article>;
}